import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import CircularProgress from '../CircularProgress';
import FacetList from './FacetList';
import Icon from '../Icon';
import styles from './Facet.module.less';

const Facet = ({
  displayName,
  internalName,
  href,
  disabled = false,
  selected = false,
  nestedTags = [],
  onClick,
  hasChildren = false,
  group,
}) => {
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(false);
  }, [selected]);

  const hasNestedTags = hasChildren || nestedTags.length > 0;
  const iconName = selected
    ? 'cancel-circled'
    : hasNestedTags
    ? 'angle-down'
    : null;

  const iconClass = selected
    ? styles.removeIcon
    : hasNestedTags
    ? styles.expandIcon
    : null;

  const handleClick = e => {
    if (e.type !== 'click' && e.key !== 'Enter') {
      return;
    }

    setLoading(true);
    if (!disabled && typeof onClick === 'function') {
      onClick(e, href);
    }
  };

  const content = (
    <>
      {loading && (
        <CircularProgress
          className={styles.facetLoading}
          size={18}
          thickness={5}
        />
      )}
      {iconName != null && (
        <Icon name={iconName} className={clsx(styles.icon, iconClass)} />
      )}
      {displayName}
    </>
  );

  const classes = clsx(styles.link, {
    [styles.loading]: loading,
    [styles.disabled]: disabled,
    [styles.selected]: selected,
    [styles.hidden]: disabled && (group === 'contentType' || group === 'grade'),
  });

  let facetLink;
  if (selected) {
    facetLink = (
      <div
        className={classes}
        onClick={handleClick}
        onKeyDown={handleClick}
        role="link"
        title={`Remove filter: ${displayName}`}
        tabIndex={0}
      >
        {content}
      </div>
    );
  } else {
    facetLink = (
      <a
        className={classes}
        href={disabled ? undefined : href}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <li key={internalName} className={styles.facet}>
      {facetLink}
      {selected && nestedTags.length > 0 && (
        <FacetList facetsArray={nestedTags} onClick={onClick} />
      )}
    </li>
  );
};

Facet.displayName = 'Facet';
Facet.propTypes = {
  displayName: PropTypes.string,
  internalName: PropTypes.string,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  nestedTags: PropTypes.array,
  href: PropTypes.string,
  onClick: PropTypes.func,
  count: PropTypes.number,
  hasChildren: PropTypes.bool,
  group: PropTypes.string,
};

export default Facet;
